import {
  useEffect,
  useRef,
  useState
} from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Alert,
  Button,
  Badge,
} from "reactstrap";
import Loader from "components/Common/Loader";
import { getVirtualPayPaymentStatus } from "apis/deposit";
import { showErrorNotification } from "store/general/notifications/actions";
const POLLING_MS = 3000;
const FINAL_STATUSES = ["SUCCESS", "FAILED", "CANCELLED"];

const STATUS_META = {
  SUCCESS: {
    title: "Payment Successful",
    color: "success",
  },
  FAILED: {
    title: "Payment Failed",
    color: "danger",
  },
  CANCELLED: {
    title: "Payment Cancelled",
    color: "danger",
  },
  PENDING: {
    title: "Waiting for Confirmation",
    color: "warning",
  },
  PROCESSING: {
    title: "Processing Payment",
    color: "info",
  },
};

const PaymentResult = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const params = new URLSearchParams(location.search);
  const reference = params.get("reference") || "";

  const intervalRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [statusData, setStatusData] = useState({
    reference,
    status: "PENDING",
    amount: null,
    currency: "",
    message: "Checking payment status...",
  });

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await getVirtualPayPaymentStatus(reference);

      if (!response?.isSuccess) {
        throw new Error(response?.message || "Unable to verify payment.");
      }

      const result = response?.result || {};
      const nextStatus = result.status || "PENDING";

      setStatusData({
        reference: result.reference || reference,
        status: nextStatus,
        amount: result.amount,
        currency: result.currency || "",
        message: result.message || "",
      });

      if (FINAL_STATUSES.includes(nextStatus)) {
        stopPolling();
      }
    } catch (error) {
      stopPolling();

      setStatusData((prev) => ({
        ...prev,
        status: "FAILED",
        message: error.message || "Unable to verify payment.",
      }));

      dispatch(
        showErrorNotification(
          error.message || "Unable to verify payment."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!reference) {
      setLoading(false);

      setStatusData((prev) => ({
        ...prev,
        status: "FAILED",
        message: "Payment reference is missing.",
      }));

      dispatch(showErrorNotification("Payment reference is missing."));
      return;
    }

    intervalRef.current = setInterval(() => {
      fetchStatus();
    }, POLLING_MS);

    fetchStatus();

    return () => {
      stopPolling();
    };
  }, [reference]);

  const currentStatus = statusData.status || "PENDING";
  const ui = STATUS_META[currentStatus] || STATUS_META.PENDING;

  return (
    <Container>
      <div className="page-content mt-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="card-shadow">
              <CardBody className="p-4">
                {loading ? (
                  <>
                    <Loader />
                    <h4 className="text-center mt-3 mb-2">
                      Checking Payment Status
                    </h4>
                    <p className="text-muted text-center mb-0">
                      Please wait while we verify your payment.
                    </p>
                  </>
                ) : (
                  <>
                    <Alert color={ui.color} className="text-center mb-4">
                      <h4 className="mb-1">{ui.title}</h4>
                      <p className="mb-0">
                        {statusData.message || "Status updated."}
                      </p>
                    </Alert>

                    <div className="mb-3 d-flex justify-content-between align-items-center">
                      <span className="text-muted">
                        Transaction Reference
                      </span>
                      <span className="fw-semibold text-break text-end ms-3">
                        {statusData.reference || "-"}
                      </span>
                    </div>

                    <div className="mb-3 d-flex justify-content-between align-items-center">
                      <span className="text-muted">Status</span>
                      <span className={`fw-semibold text-${ui.color}`}>
                        {currentStatus}
                      </span>
                    </div>

                    <div className="mb-3 d-flex justify-content-between align-items-center">
                      <span className="text-muted">Amount</span>
                      <span className="fw-semibold">
                        {statusData.amount === null ||
                        statusData.amount === undefined
                          ? "-"
                          : Number(statusData.amount).toFixed(2)}
                      </span>
                    </div>

                    <div className="mb-4 d-flex justify-content-between align-items-center">
                      <span className="text-muted">Currency</span>
                      <span className="fw-semibold">
                        {statusData.currency || "-"}
                      </span>
                    </div>
                  </>
                )}

                <div className="d-flex gap-2">
                  <Button
                    color="primary"
                    className="w-100"
                    onClick={() => history.push("/dashboard")}
                  >
                    Back to Dashboard
                  </Button>

                  <Button
                    color="secondary"
                    outline
                    className="w-100"
                    onClick={() => history.push("/reports")}
                  >
                    View Reports
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default PaymentResult;